import React, { useState, useEffect } from "react";
import { useTheme } from '@repo/shared';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Form, Container, Row, Col, Card, Button, Spinner, Tabs, Tab, Alert } from "react-bootstrap";
import ConfigDataService from "../../services/config/ConfigService";
import { ButtonAwaiting } from '../common/Buttons';

import "./EmailTemplates.css";

export default function EmailTemplates() {
  const { appTheme } = useTheme();
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    document.title = `${appTheme?.appName} - Email Templates`;
    fetchTemplates();
  }, []);

  useEffect(() => {
    // Set first template as active when templates load
    if (emailTemplates.length > 0 && !activeTab) {
      setActiveTab(emailTemplates[0].id);
    }
  }, [emailTemplates, activeTab]);

  const fetchTemplates = async () => {
    try {
      setError(null);
      const response = await ConfigDataService.getEmailTemplates();
      setEmailTemplates(response || []);
    } catch (err) {
      setError(err.message || "Error fetching email templates");
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (template) => {
    setEditingId(template.id);
    setEditedContent(template.content);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditedContent("");
  };

  const saveTemplate = async (template) => {
    try {
      setSavingId(template.id);
      setError(null);
      setSuccess(null);

      const updatedTemplate = {
        ...template,
        content: editedContent,
      };

      await ConfigDataService.saveEmailTemplate(updatedTemplate);

      // Update local state
      setEmailTemplates(
        emailTemplates.map((t) =>
          t.id === template.id ? updatedTemplate : t
        )
      );

      setSuccess(`"${template.name}" template saved successfully!`);
      setEditingId(null);
      setEditedContent("");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || "Error saving email template");
      console.error("Save error:", err);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <Spinner animation="border" />;

  const renderBody = () => {
    return (
      <>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {emailTemplates.length > 0 ? (
        <div className={`email-templates-tabs-wrapper ${editingId ? 'editing' : ''}`}>
          <Tabs
            id="email-templates-tabs"
            activeKey={activeTab}
            onSelect={(k) => {
              // Disable tab switching while editing
              if (!editingId) {
                setActiveTab(k);
              }
            }}
            className="mb-3"
          >
          {emailTemplates.map((template) => (
            <Tab key={template.id} eventKey={template.id} title={template.name}>
              <div className="email-template-content p-3">
                <p className="template-description">{template.description}</p>

                {editingId === template.id ? (
                  <div className="template-editor">
                    <Form.Group className="mb-3">
                      <Form.Label>Email Content</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={10}
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        placeholder="Enter email content here..."
                        disabled={savingId === template.id}
                        className="email-textarea"
                      />
                      <small className="text-muted d-block mt-2">
                        {editedContent.length} characters
                      </small>
                    </Form.Group>

                    <div className="template-actions">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => saveTemplate(template)}
                        disabled={savingId === template.id}
                        className="me-2"
                      >
                        {savingId === template.id ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Saving...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            Save
                          </>
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={cancelEditing}
                        disabled={savingId === template.id}
                      >
                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="template-view">
                    <div className="template-content-preview">
                      <p>{template.content}</p>
                    </div>

                    <ButtonAwaiting
                      isPrimary={true}
                      title="Edit"
                      icon={<FontAwesomeIcon icon={faEdit} className="me-2" />}
                      awaitingTitle="Sending..."
                      type="button"
                      onClick={() => startEditing(template)}
                    />
                    
                  </div>
                )}
              </div>
            </Tab>
          ))}
        </Tabs>
        </div>
      ) : (
        <Alert variant="info">No email templates found.</Alert>
      )}
      </>
    )
  }

  return (
    <Container className="py-2 py-lg-4">
      <Row className="justify-content-center">
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Header className="d-flex align-items-center gap-2">
              <h2 className="mb-0">Email Templates</h2>
            </Card.Header>
            <Card.Body className="p-3" style={{ overflowX: 'auto' }}>
              { renderBody()}
            </Card.Body>
            <Card.Footer className="text-muted">
              
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}